"use server";

import { execFile } from "node:child_process";
import { promisify } from "node:util";
import { cookies, headers } from "next/headers";
import crypto from "node:crypto";
import fs from "node:fs";
import { revalidatePath } from "next/cache";

const pexec = promisify(execFile);
const MAXBUF = 20 * 1024 * 1024;

function slugFromUrl(url: string) {
  const host = new URL(url).hostname.replace(/^www\./, "");
  return host.split(".")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
}

// Append --token when running headless (container/cloud); locally the CLI is already authed.
function vc(args: string[]): string[] {
  const t = process.env.VERCEL_TOKEN;
  return t ? [...args, "--token", t] : args;
}

function aliasFrom(stdout: string): string | undefined {
  return (
    stdout.match(/Aliased\s+(https:\/\/\S+)/)?.[1] ||
    stdout.match(/https:\/\/[^\s]+\.vercel\.app/)?.[0]
  );
}

type Deployed = Record<string, { url: string; project: string }>;
function readDeployed(): Deployed {
  try {
    return JSON.parse(fs.readFileSync("data/deployed.json", "utf8"));
  } catch {
    return {};
  }
}
function writeDeployed(d: Deployed) {
  fs.mkdirSync("data", { recursive: true });
  fs.writeFileSync("data/deployed.json", JSON.stringify(d, null, 2));
}

function regenRegistry() {
  const slugs = fs
    .readdirSync("brands")
    .filter((f) => f.endsWith(".ts") && f !== "_types.ts")
    .map((f) => f.replace(/\.ts$/, ""));
  const cn = (s: string) => s.replace(/[^a-z0-9]/gi, "");
  const out = `import type { BrandConfig } from "@/brands/_types";
${slugs.map((s) => `import { ${cn(s)} } from "@/brands/${s}";`).join("\n")}

const REGISTRY: Record<string, BrandConfig> = {
${slugs.map((s) => `  "${s}": ${cn(s)},`).join("\n")}
};

export function getBrand(slug: string): BrandConfig | null {
  return REGISTRY[slug] ?? null;
}

export function allBrandSlugs(): string[] {
  return Object.keys(REGISTRY);
}
`;
  fs.writeFileSync("lib/brands.ts", out);
}

/* ---------------- auth ---------------- */
function authToken(): string {
  const pw = process.env.STUDIO_PASSWORD ?? "Amari";
  return crypto.createHash("sha256").update(pw + "::formed-studio").digest("hex");
}
export async function isAuthed(): Promise<boolean> {
  return (await cookies()).get("studio_auth")?.value === authToken();
}
export async function login(pw: string): Promise<{ ok: boolean }> {
  const expected = process.env.STUDIO_PASSWORD ?? "Amari";
  if (pw !== expected) return { ok: false };
  // iOS Safari drops a non-Secure cookie over HTTPS → set Secure when the
  // request came in over https (Caddy sets x-forwarded-proto). Stays off for
  // plain http://localhost dev so local login keeps working.
  const proto = (await headers()).get("x-forwarded-proto");
  const secure = proto === "https";
  (await cookies()).set("studio_auth", authToken(), {
    httpOnly: true,
    sameSite: "lax",
    secure,
    path: "/",
    maxAge: 60 * 60 * 24 * 30,
  });
  revalidatePath("/studio");
  return { ok: true };
}
export async function logout() {
  (await cookies()).delete("studio_auth");
  revalidatePath("/studio");
}

/* ---------------- generate ---------------- */
export async function generateDemo(
  rawUrl: string,
  instructions?: string
): Promise<{ ok: boolean; slug?: string; dm?: string; error?: string }> {
  let url = rawUrl.trim();
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  let slug: string;
  try {
    slug = slugFromUrl(url);
  } catch {
    return { ok: false, error: "Ungültige URL" };
  }
  try {
    await pexec("node", ["scripts/demo.mjs", url, slug], {
      env: { ...process.env, DEMO_INSTRUCTIONS: instructions?.trim() || "" },
      maxBuffer: MAXBUF,
      timeout: 180000,
    });
    let dm = "";
    try {
      dm = fs.readFileSync(`scraped/${slug}.dm.txt`, "utf8");
    } catch {}
    revalidatePath("/studio");
    return { ok: true, slug, dm };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message || "Fehler" };
  }
}

export async function adjustDemo(
  slug: string,
  instructions: string
): Promise<{ ok: boolean; dm?: string; email?: string; error?: string }> {
  try {
    await pexec("node", ["scripts/art-direct.mjs", slug], {
      env: { ...process.env, DEMO_INSTRUCTIONS: instructions?.trim() || "" },
      maxBuffer: MAXBUF,
      timeout: 180000,
    });
    let dm = "";
    let email = "";
    try {
      dm = fs.readFileSync(`scraped/${slug}.dm.txt`, "utf8");
    } catch {}
    try {
      email = fs.readFileSync(`scraped/${slug}.email.txt`, "utf8");
    } catch {}
    revalidatePath("/studio");
    return { ok: true, dm, email };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message || "Anpassung fehlgeschlagen" };
  }
}

/* ---------------- per-demo deploy ---------------- */
export async function publishDemo(
  slug: string
): Promise<{ ok: boolean; url?: string; error?: string }> {
  // "-formed" suffix → far less likely to collide with a globally-taken
  // <slug>.vercel.app, so the project gets a clean PUBLIC production alias.
  const proj = (slug + "-formed").replace(/[^a-z0-9-]/g, "").toLowerCase();
  const tokenFlag = process.env.VERCEL_TOKEN ? ` --token ${process.env.VERCEL_TOKEN}` : "";
  try {
    await pexec("vercel", vc(["link", "--yes", "--project", proj]), {
      env: process.env,
      maxBuffer: MAXBUF,
      timeout: 120000,
    });
    // Project-level env so the deployed root renders this brand (DEMO_SLUG).
    await pexec(
      "sh",
      ["-c", `vercel env rm DEMO_SLUG production -y${tokenFlag} 2>/dev/null; printf '%s' ${slug} | vercel env add DEMO_SLUG production${tokenFlag}`],
      { env: process.env, maxBuffer: MAXBUF, timeout: 60000 }
    ).catch(() => {});
    const { stdout } = await pexec("vercel", vc(["--prod", "--yes"]), {
      env: process.env,
      maxBuffer: MAXBUF,
      timeout: 300000,
    });
    const url = aliasFrom(stdout) || "";
    const d = readDeployed();
    d[slug] = { url, project: proj };
    writeDeployed(d);
    revalidatePath("/studio");
    return { ok: true, url };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message || "Deploy fehlgeschlagen" };
  }
}

/* ================= BESPOKE (Opus builds a real site from scratch) ================= */

export async function generateBespoke(
  rawUrl: string,
  instructions?: string
): Promise<{ ok: boolean; slug?: string; error?: string }> {
  let url = rawUrl.trim();
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  let slug: string;
  try {
    slug = slugFromUrl(url);
  } catch {
    return { ok: false, error: "Ungültige URL" };
  }
  try {
    await pexec("node", ["scripts/bespoke-demo.mjs", url, slug], {
      env: { ...process.env, DEMO_INSTRUCTIONS: instructions?.trim() || "" },
      maxBuffer: MAXBUF,
      timeout: 600000,
    });
    revalidatePath("/studio");
    return { ok: true, slug };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message?.slice(0, 400) || "Fehler" };
  }
}

export async function adjustBespoke(
  slug: string,
  instructions: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    await pexec("node", ["scripts/bespoke.mjs", slug], {
      env: { ...process.env, DEMO_INSTRUCTIONS: instructions?.trim() || "" },
      maxBuffer: MAXBUF,
      timeout: 600000,
    });
    revalidatePath("/studio");
    return { ok: true };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message?.slice(0, 400) || "Anpassung fehlgeschlagen" };
  }
}

export async function deployBespoke(
  slug: string
): Promise<{ ok: boolean; url?: string; error?: string }> {
  const dir = `public/brands/${slug}/site`;
  if (!fs.existsSync(`${dir}/index.html`)) return { ok: false, error: "Keine Bespoke-Seite vorhanden" };
  const proj = (slug + "-site").replace(/[^a-z0-9-]/g, "").toLowerCase();
  try {
    await pexec("vercel", vc(["link", "--yes", "--cwd", dir, "--project", proj]), {
      env: process.env,
      maxBuffer: MAXBUF,
      timeout: 120000,
    });
    const { stdout } = await pexec("vercel", vc(["deploy", "--prod", "--yes", "--cwd", dir]), {
      env: process.env,
      maxBuffer: MAXBUF,
      timeout: 300000,
    });
    const url = aliasFrom(stdout) || "";
    const d = readDeployed();
    d[slug] = { url, project: proj };
    writeDeployed(d);
    revalidatePath("/studio");
    return { ok: true, url };
  } catch (e) {
    return { ok: false, error: (e as { message?: string }).message?.slice(0, 400) || "Deploy fehlgeschlagen" };
  }
}

export async function deleteBespoke(slug: string): Promise<{ ok: boolean; error?: string }> {
  try {
    fs.rmSync(`public/brands/${slug}/site`, { recursive: true, force: true });
  } catch {}
  const d = readDeployed();
  delete d[slug];
  writeDeployed(d);
  revalidatePath("/studio");
  return { ok: true };
}

/* ---------------- delete ---------------- */
export async function deleteDemo(slug: string): Promise<{ ok: boolean; error?: string }> {
  if (slug === "becker") {
    return { ok: false, error: "Referenz-Demo, nicht löschbar" };
  }
  const d = readDeployed();
  const project = d[slug]?.project;
  if (project && /^[a-z0-9-]+$/.test(project)) {
    // `vercel project rm` has no --yes flag; it prompts. Pipe a confirm.
    const tokenFlag = process.env.VERCEL_TOKEN ? `--token ${process.env.VERCEL_TOKEN}` : "";
    try {
      await pexec("sh", ["-c", `printf 'y\\n' | vercel project rm ${project} ${tokenFlag}`], {
        env: process.env,
        maxBuffer: MAXBUF,
        timeout: 120000,
      });
    } catch {
      // project may already be gone — keep cleaning up locally
    }
  }
  try {
    fs.rmSync(`brands/${slug}.ts`, { force: true });
  } catch {}
  try {
    fs.rmSync(`public/brands/${slug}`, { recursive: true, force: true });
  } catch {}
  for (const ext of ["json", "dm.txt", "email.txt"]) {
    try {
      fs.rmSync(`scraped/${slug}.${ext}`, { force: true });
    } catch {}
  }
  delete d[slug];
  writeDeployed(d);
  regenRegistry();
  revalidatePath("/studio");
  return { ok: true };
}
